import React from 'react';
import {useTranslation} from 'react-i18next';
import {EAutocompleteOption} from 'webapp-lib/Components';

import {TranslatedBankAccount} from '@lexian/api/invoicing-api';
import {useMasterdata} from '_/hooks/useMasterdata';
import {EFAutocompleteProps} from '_/utilities/form';
import {getOptions} from '_/utilities/masterdata.utils';

import {filterByActiveState} from './ActiveFilter/ActiveFilter.utils';
import {
  ActiveStateFilterState,
  useActiveStateFilter,
} from './ActiveFilter/useActiveStateFilter';
import {
  AutocompleteBase,
  staticLoader,
} from './AutocompleteBase/AutocompleteBase';

const toOption = (bankAccount: TranslatedBankAccount): EAutocompleteOption =>
  getOptions([bankAccount])[0];

export const ClientMoneyBankAccountAutocomplete = ({
  label,
  multiple = false,
  onUserChange,
  onSelectedBankAccountIsFilteredOut,
  ...otherProps
}: Omit<EFAutocompleteProps, 'options'> & {
  onUserChange?: (bankAccount: TranslatedBankAccount | undefined) => void;
  onSelectedBankAccountIsFilteredOut?: () => void;
  multiple?: boolean;
}): React.ReactElement => {
  const {t} = useTranslation('common');
  const {translatedClientMoneyBankAccounts} = useMasterdata();
  const activeStateFilter = useActiveStateFilter(
    'active-filter.client-money-bank-account-autocomplete'
  );

  return (
    <AutocompleteBase
      {...otherProps}
      filter={activeStateFilter}
      label={
        label ??
        (multiple
          ? t('common:labels.clientMoneyBankAccounts')
          : t('common:labels.clientMoneyBankAccount'))
      }
      loadItems={staticLoader(
        (filterState: ActiveStateFilterState | undefined) =>
          filterByActiveState(
            translatedClientMoneyBankAccounts,
            filterState?.activeState ?? 'ACTIVE'
          )
      )}
      multiple={multiple}
      reloadTrigger={translatedClientMoneyBankAccounts}
      toOption={toOption}
      noOptionFiltering
      onItemChange={onUserChange}
      onSelectedItemMissing={onSelectedBankAccountIsFilteredOut}
    />
  );
};
